import { Printer } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { amountInWords } from '../../lib/amountInWords.js';
import { formatDate } from '../../lib/intl.js';
import { formatMoney, setCurrencyDecimals, trimMoney } from '../../lib/money.js';
import { assetUrl, getPublicInvoice, type PublicInvoice } from '../../lib/publicInvoice.js';

// Self-contained bilingual labels (this page is customer-facing and has no app shell).
const LABELS = {
  en: { invoice: 'Invoice', billTo: 'Bill to', date: 'Date', due: 'Due', subtotal: 'Subtotal', discount: 'Discount', tax: 'Tax', total: 'Total', paid: 'Paid', balance: 'Balance due', notes: 'Notes', print: 'Print', qty: 'Qty', price: 'Price', amount: 'Amount', item: 'Description', amountInWords: 'Amount in words', notFound: 'This invoice link is invalid or has expired.' },
  ar: { invoice: 'فاتورة', billTo: 'إلى', date: 'التاريخ', due: 'الاستحقاق', subtotal: 'المجموع الفرعي', discount: 'الخصم', tax: 'الضريبة', total: 'الإجمالي', paid: 'المدفوع', balance: 'المتبقي', notes: 'ملاحظات', print: 'طباعة', qty: 'الكمية', price: 'السعر', amount: 'المبلغ', item: 'الوصف', amountInWords: 'المبلغ كتابةً', notFound: 'رابط الفاتورة غير صالح أو منتهي الصلاحية.' },
};

export function PublicInvoicePage(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const { i18n } = useTranslation();
  const lang = i18n.language.startsWith('ar') ? 'ar' : 'en';
  const L = LABELS[lang];
  const dir = lang === 'ar' ? 'rtl' : 'ltr';

  const [inv, setInv] = useState<PublicInvoice | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!token) { setError(true); return; }
    void getPublicInvoice(token)
      .then((d) => {
        // Public page (no app shell) — register backend decimals so IQD shows whole.
        if (d.currencyDecimals) setCurrencyDecimals(d.currencyDecimals);
        setInv(d);
      })
      .catch(() => setError(true));
  }, [token]);

  const money = (a: string): string => formatMoney(a, { locale: i18n.language, currency: inv?.currencyCode ?? 'USD' });

  if (error) {
    return (
      <div className="grid min-h-screen place-items-center bg-slate-100 p-6 text-center">
        <p className="text-slate-500">{L.notFound}</p>
      </div>
    );
  }
  if (!inv) {
    return <div className="grid min-h-screen place-items-center bg-slate-100"><div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" /></div>;
  }

  const showDiscount = Number(inv.discountTotal) > 0;
  const due = Number(inv.amountDue);

  return (
    <div dir={dir} className="min-h-screen bg-slate-100 py-8 print:bg-white print:py-0">
      <div className="mx-auto max-w-3xl px-4">
        <div className="mb-4 flex justify-end print:hidden">
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
          >
            <Printer className="h-4 w-4" aria-hidden />{L.print}
          </button>
        </div>

        <div className="overflow-hidden rounded-2xl bg-white shadow-sm print:rounded-none print:shadow-none">
          {/* header */}
          <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 p-6">
            <div className="flex items-center gap-3">
              {inv.seller.logoUrl
                ? <img src={assetUrl(inv.seller.logoUrl) ?? ''} alt="" className="h-12 w-12 rounded-lg object-cover" referrerPolicy="no-referrer" />
                : <div className="grid h-12 w-12 place-items-center rounded-lg bg-slate-800 text-lg font-bold text-white">{inv.seller.name.slice(0, 1)}</div>}
              <div>
                <div className="text-lg font-bold text-slate-900">{inv.seller.name}</div>
                {inv.seller.tagline && <div className="text-sm text-slate-500">{inv.seller.tagline}</div>}
              </div>
            </div>
            <div className="text-end">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-400">{L.invoice}</div>
              <div className="font-mono text-xl font-bold text-slate-900">{inv.number}</div>
            </div>
          </div>

          {/* meta + bill-to */}
          <div className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2">
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-400">{L.billTo}</div>
              <div className="mt-1 font-medium text-slate-900">{inv.billTo.name}</div>
              {inv.billTo.addressLines.map((l, i) => <div key={i} className="text-sm text-slate-500">{l}</div>)}
            </div>
            <div className="sm:text-end">
              <div className="text-sm"><span className="text-slate-400">{L.date}: </span><span className="text-slate-700">{formatDate(inv.date, { locale: i18n.language })}</span></div>
              {inv.dueDate && <div className="text-sm"><span className="text-slate-400">{L.due}: </span><span className="text-slate-700">{formatDate(inv.dueDate, { locale: i18n.language })}</span></div>}
            </div>
          </div>

          {/* lines */}
          <div className="overflow-x-auto px-6">
            <table className="w-full text-start text-sm">
              <thead>
                <tr className="border-y border-slate-200 text-xs uppercase tracking-wide text-slate-400">
                  <th className="py-2 text-start font-medium">{L.item}</th>
                  <th className="py-2 text-end font-medium">{L.qty}</th>
                  <th className="py-2 text-end font-medium">{L.price}</th>
                  <th className="py-2 text-end font-medium">{L.amount}</th>
                </tr>
              </thead>
              <tbody>
                {inv.lines.map((l, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-2 text-slate-800">{l.description || '—'}</td>
                    <td className="py-2 text-end font-mono text-slate-600">{trimMoney(l.quantity)}</td>
                    <td className="py-2 text-end font-mono text-slate-600">{money(l.unitPrice)}</td>
                    <td className="py-2 text-end font-mono text-slate-800">{money(l.lineTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* totals */}
          <div className="flex justify-end p-6">
            <dl className="w-full max-w-xs space-y-1 text-sm">
              <Row label={L.subtotal} value={money(inv.subtotal)} />
              {showDiscount && <Row label={L.discount} value={`−${money(inv.discountTotal)}`} muted />}
              {Number(inv.taxTotal) > 0 && <Row label={L.tax} value={money(inv.taxTotal)} />}
              <div className="my-1 border-t border-slate-200" />
              <Row label={L.total} value={money(inv.total)} bold />
              {Number(inv.amountPaid) > 0 && <Row label={L.paid} value={money(inv.amountPaid)} muted />}
              {due > 0 && <Row label={L.balance} value={money(inv.amountDue)} bold />}
            </dl>
          </div>

          {/* amount in words (تفقيط) — the cheque-style spelled-out total */}
          <div className="border-t border-slate-200 px-6 py-4">
            <div className="text-xs uppercase tracking-wide text-slate-400">{L.amountInWords}</div>
            <div className="mt-1 text-sm font-semibold text-slate-800">
              {amountInWords(Number(inv.total), inv.currencyCode ?? 'USD', i18n.language)}
            </div>
          </div>

          {inv.notes && (
            <div className="border-t border-slate-200 p-6">
              <div className="text-xs uppercase tracking-wide text-slate-400">{L.notes}</div>
              <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{inv.notes}</p>
            </div>
          )}

          {(inv.seller.email || inv.seller.phone || inv.seller.addressLines.length > 0) && (
            <div className="border-t border-slate-200 bg-slate-50 p-6 text-center text-xs text-slate-500 print:bg-white">
              {[inv.seller.phone, inv.seller.email, ...inv.seller.addressLines].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold, muted }: { label: string; value: string; bold?: boolean; muted?: boolean }): JSX.Element {
  return (
    <div className="flex items-center justify-between">
      <dt className={muted ? 'text-slate-400' : 'text-slate-500'}>{label}</dt>
      <dd className={`font-mono ${bold ? 'text-base font-bold text-slate-900' : muted ? 'text-slate-500' : 'text-slate-800'}`}>{value}</dd>
    </div>
  );
}
